import React from "react";
import { Clock } from "lucide-react";
import { roomColor } from "../FilterBar";

const HEADERS_DATE = [
  { label: "START" },
  { label: "FINISH" },
  { label: "DUR", icon: <Clock className="w-3 h-3 inline" />, title: "Duration — type to edit (e.g. 90, 1h 30m, 2h)" },
  { label: "ROOM" },
  { label: "TASK / SUBTASK" },
  { label: "STATUS" },
  { label: "NOTES · BLOCKERS" },
];

const HEADERS_ROOM = [
  { label: "START" },
  { label: "FINISH" },
  { label: "DUR", icon: <Clock className="w-3 h-3 inline" />, title: "Duration — type to edit (e.g. 90, 1h 30m, 2h)" },
  { label: "DATE" },
  { label: "TASK / SUBTASK" },
  { label: "STATUS" },
  { label: "NOTES · BLOCKERS" },
];

/**
 * Sticky top header row: 7 fixed columns + one column per visible crew
 * member. Each fixed header has a right-edge resize handle (amber on hover).
 * Crew columns are draggable; the parent owns the drop-line overlay and the
 * `moveCrew` reorder side-effect.
 */
export default function GridHeader({
  visibleCrew,
  leftOffsets,
  beginColResize,
  onCrewDragStart,
  onCrewDragOver,
  onCrewDrop,
  onCrewDragEnd,
  groupMode = "date",
}) {
  const HEADERS = groupMode === "room" ? HEADERS_ROOM : HEADERS_DATE;
  return (
    <>
      {HEADERS.map((h, i) => (
        <div
          key={h.label}
          className="sticky top-[88px] z-30 bg-bg-surface border-b border-line-strong border-r border-line flex items-center px-2 mono text-2xs uppercase tracking-widest text-ink-mute group"
          style={{
            height: 32,
            left: leftOffsets[i],
            position: "sticky",
            zIndex: 32 - i,
          }}
          title={h.title}
        >
          {h.icon ? <span className="flex items-center gap-1">{h.icon}{h.label}</span> : h.label}
          <div
            onMouseDown={(e) => beginColResize("fixed", i, e)}
            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-amber transition-opacity"
            data-testid={`col-resize-fixed-${i}`}
            title="Drag to resize column"
          />
        </div>
      ))}
      {visibleCrew.map((c, ci) => (
        <div
          key={c.id}
          // HTML5 drag — the parent listens for dragenter / dragover events on
          // the grid wrapper to position the drop-line overlay. dragstart sets
          // a payload that the wrapper recognises as a column reorder.
          draggable
          onDragStart={(e) => onCrewDragStart && onCrewDragStart(ci, e)}
          onDragEnd={(e) => onCrewDragEnd && onCrewDragEnd(e)}
          onDragOver={(e) => onCrewDragOver && onCrewDragOver(ci, e)}
          onDrop={(e) => onCrewDrop && onCrewDrop(ci, e)}
          className="sticky top-[88px] z-20 bg-bg-surface border-b border-line-strong border-r border-line flex flex-col items-start justify-center px-2 group cursor-grab active:cursor-grabbing"
          style={{ height: 32 }}
          data-testid={`col-crew-${c.id}`}
          title={`${c.name}${c.role ? " · " + c.role : ""} — drag to reorder`}
        >
          <div className="flex items-center gap-1">
            <span
              className="w-1.5 h-1.5 inline-block shrink-0"
              style={{ background: roomColor(c.color_token) }}
            />
            <span
              className="mono text-2xs uppercase tracking-wider text-ink truncate max-w-[80px]"
            >
              {firstName(c.name)}
            </span>
          </div>
          <span className="text-2xs text-ink-mute truncate max-w-[88px]" title={c.role}>
            {c.role}
          </span>
          {/* Per-column resize handle on the right edge of every crew column.
              Drags only the column it's attached to, so producers can fine-tune
              whichever crew member needs more or less width. */}
          <div
            onMouseDown={(e) => beginColResize("crew", c.id, e)}
            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-amber transition-opacity z-10"
            data-testid={`col-resize-crew-${c.id}`}
            title="Drag to resize this crew column"
          />
        </div>
      ))}
    </>
  );
}

// Returns the most useful "first" identifier in a person's name. Skips a
// leading initial like "J." or "A" and returns the surname instead, so
// "J. Schmidt" → "Schmidt" and "Alex Vidal" → "Alex". Falls back to the raw
// string when there's nothing else to work with.
function firstName(name) {
  if (!name) return "—";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0];
  // If the first token is a single letter or "X." style initial, prefer the
  // second token — that's a real word and far more useful in a tight column.
  const first = parts[0];
  const isInitial = /^[A-Za-z]\.?$/.test(first);
  return isInitial ? parts[1] : first;
}
